import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/state/authStore';
import {
  login,
  respondNewPassword,
  forgotPassword,
  confirmForgotPassword,
  CognitoError,
} from './cognitoAuth';
import type { AuthTokens } from './cognitoAuth';

type Phase = 'login' | 'new_password' | 'forgot' | 'forgot_confirm';

export function LoginPage() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setTokens = useAuthStore((s) => s.setTokens);
  const [params] = useSearchParams();

  const [phase, setPhase] = useState<Phase>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [session, setSession] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(params.get('error') ?? '');
  const [info, setInfo] = useState('');

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  const clearMessages = () => { setError(''); setInfo(''); };

  const handleLogin = async () => {
    if (!email || !password) return;
    setLoading(true);
    clearMessages();
    try {
      const result = await login(email, password);
      if (result.type === 'new_password_required') {
        setPhase('new_password');
        setSession(result.session);
        setPassword('');
      } else {
        completeLogin(result.tokens);
      }
    } catch (e) {
      if (e instanceof CognitoError) {
        setError(translateError(e.code));
      } else {
        setError('ログインに失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleNewPassword = async () => {
    if (!newPassword) return;
    if (newPassword !== newPasswordConfirm) {
      setError('パスワードが一致しません');
      return;
    }
    setLoading(true);
    clearMessages();
    try {
      const tokens = await respondNewPassword(email, newPassword, session);
      completeLogin(tokens);
    } catch (e) {
      if (e instanceof CognitoError) {
        setError(translateError(e.code));
      } else {
        setError('パスワード設定に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('メールアドレスを入力してください');
      return;
    }
    setLoading(true);
    clearMessages();
    try {
      await forgotPassword(email);
      setPhase('forgot_confirm');
      setInfo('確認コードをメールに送信しました。メールを確認してください。');
    } catch (e) {
      if (e instanceof CognitoError) {
        setError(translateError(e.code));
      } else {
        setError('リセットメールの送信に失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async () => {
    if (!resetCode || !newPassword) return;
    if (newPassword !== newPasswordConfirm) {
      setError('パスワードが一致しません');
      return;
    }
    setLoading(true);
    clearMessages();
    try {
      await confirmForgotPassword(email, resetCode, newPassword);
      setInfo('パスワードをリセットしました。新しいパスワードでログインしてください。');
      setPhase('login');
      setPassword('');
      setNewPassword('');
      setNewPasswordConfirm('');
      setResetCode('');
    } catch (e) {
      if (e instanceof CognitoError) {
        setError(translateError(e.code));
      } else {
        setError('パスワードリセットに失敗しました');
      }
    } finally {
      setLoading(false);
    }
  };

  const completeLogin = (tokens: AuthTokens) => {
    setTokens(tokens);
  };

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter') action();
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="w-full max-w-md rounded-xl bg-white p-8 shadow-lg">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Delphi</h1>
          <p className="mt-1 text-sm text-gray-500">コーチ育成支援システム</p>
        </div>

        {error && (
          <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
        )}
        {info && (
          <div className="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-700">{info}</div>
        )}

        {/* ログインフォーム */}
        {phase === 'login' && (
          <div className="mt-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleLogin)}
                placeholder="example@test.com"
                autoComplete="email"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">パスワード</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleLogin)}
                placeholder="パスワードを入力"
                autoComplete="current-password"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleLogin}
              disabled={loading || !email || !password}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'ログイン中...' : 'ログイン'}
            </button>
            <button
              type="button"
              onClick={() => { clearMessages(); setPhase('forgot'); }}
              className="w-full text-center text-sm text-gray-500 hover:text-blue-600"
            >
              パスワードを忘れた方はこちら
            </button>
          </div>
        )}

        {/* 初回パスワード設定 */}
        {phase === 'new_password' && (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
              初回ログインのため、新しいパスワードを設定してください。
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">新しいパスワード</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleNewPassword)}
                placeholder="8文字以上、大小英数"
                autoComplete="new-password"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">パスワード確認</label>
              <input
                type="password"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleNewPassword)}
                placeholder="もう一度入力"
                autoComplete="new-password"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleNewPassword}
              disabled={loading || !newPassword || !newPasswordConfirm}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? '設定中...' : 'パスワードを設定してログイン'}
            </button>
          </div>
        )}

        {/* パスワードリセット: メールアドレス入力 */}
        {phase === 'forgot' && (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-700">
              登録済みのメールアドレスに確認コードを送信します。
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">メールアドレス</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleForgotPassword)}
                placeholder="example@test.com"
                autoComplete="email"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={loading || !email}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? '送信中...' : '確認コードを送信'}
            </button>
            <button
              type="button"
              onClick={() => { clearMessages(); setPhase('login'); }}
              className="w-full text-center text-sm text-gray-500 hover:text-blue-600"
            >
              ログインに戻る
            </button>
          </div>
        )}

        {/* パスワードリセット: 確認コード + 新パスワード */}
        {phase === 'forgot_confirm' && (
          <div className="mt-6 space-y-4">
            <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
              <span className="font-medium">{email}</span> に確認コードを送信しました。
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">確認コード</label>
              <input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                placeholder="メールに届いた6桁のコード"
                autoComplete="one-time-code"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm tracking-widest focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">新しいパスワード</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="8文字以上、大小英数"
                autoComplete="new-password"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">パスワード確認</label>
              <input
                type="password"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, handleConfirmReset)}
                placeholder="もう一度入力"
                autoComplete="new-password"
                className="mt-1 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
            <button
              type="button"
              onClick={handleConfirmReset}
              disabled={loading || !resetCode || !newPassword || !newPasswordConfirm}
              className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'リセット中...' : 'パスワードをリセット'}
            </button>
            <button
              type="button"
              onClick={() => { clearMessages(); setPhase('login'); }}
              className="w-full text-center text-sm text-gray-500 hover:text-blue-600"
            >
              ログインに戻る
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function translateError(code: string): string {
  switch (code) {
    case 'NotAuthorizedException':
      return 'メールアドレスまたはパスワードが正しくありません';
    case 'UserNotFoundException':
      return 'このメールアドレスは登録されていません';
    case 'UserNotConfirmedException':
      return 'アカウントが確認されていません。管理者に連絡してください';
    case 'PasswordResetRequiredException':
      return 'パスワードのリセットが必要です';
    case 'InvalidPasswordException':
      return 'パスワードが要件を満たしていません（8文字以上、大文字・小文字・数字を含む）';
    case 'CodeMismatchException':
      return '確認コードが正しくありません';
    case 'ExpiredCodeException':
      return '確認コードの有効期限が切れています。もう一度送信してください';
    case 'LimitExceededException':
      return '試行回数の上限に達しました。しばらく待ってから再試行してください';
    case 'TooManyRequestsException':
      return 'リクエストが多すぎます。しばらく待ってから再試行してください';
    case 'InvalidParameterException':
      return '入力内容に問題があります。メールアドレスを確認してください';
    default:
      return `エラーが発生しました（${code}）`;
  }
}
